import { SubHeading } from '@social-solutions/component-library';
import Popup from 'components/Generics/Popup/Popup';
import { FeatureFlagContext } from 'FeatureFlagContext';
import { IDPOptions } from 'models/identity-provider/identity-provider';
import { UserPoolPropertiesResponse } from 'models/schema-generated-models/api-response-bodies/user-pool-properties-response';
import { UserPoolsResponse } from 'models/schema-generated-models/api-response-bodies/user-pools-response';
import { Configuration as IConfiguration } from 'models/schema-generated-models/idp/configuration';
import { ReactFragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ClientFactory, IdentityProviderService, UserPoolService } from 'services';
import { SSOAPIBadRequest, SSOAPINetworkError, SSOAPINotFoundError, SSOAPIServerError, SSOAPIUnexpectedResponseBody, SSOAPIUserInsufficientPermissionsError, SSOAPIUserSessionInvalidError } from 'services/errors';
import { NotificationService } from 'services/notification-service';
import IdentityProviderList from './IdentityProviderList/IdentityProviderList';
import * as S from './IdentityProviders.styles';
import LoginOptionsTabContent from './LoginOptions/LoginOptionsTabContent';
import PoolParametersTabContent, { ModalUserPools } from './PoolParameters/PoolParametersTabContent';
import SAMLIdentityProviderDetail from './SAMLIdentityProviderDetail/SAMLIdentityProviderDetail';
import { IntegrationContext } from 'helpers/integration/IntegrationContext';

export interface IIdentityProvidersProps {
	clid?: string | null;
	sessionStaffId?: string | null;
}

export interface IPoolProperties {
    userPoolId: string;
    userPoolSaml2Url: string;
    domain: string;
    userPoolName: string;
    available: boolean;
}

export interface IPoolResponse {
	user_pool_id: string;
	user_pool_saml2_url: string;
}

export enum SSOTabs {
	POOL = 'POOL PARAMETERS',
	LOGIN = 'LOGIN OPTIONS'
}

type Tab = {
    id: SSOTabs;
    title: string;
    content: ReactFragment;
    featureFlag: boolean;
}

export default function IdentityProviders(): JSX.Element {
    const { t } = useTranslation();
    const [poolProperties, setPoolProperties] = useState<IPoolProperties>({ userPoolId: '', userPoolSaml2Url: '', domain: '', userPoolName: '', available: false });
    const [idpList, setIdpList] = useState([] as IConfiguration[]);
    const [poolPropertiesLoaded, setPoolPropertiesLoaded] = useState(false);
    const [idpListLoaded, setIdpListLoaded] = useState(false);
    const [userPoolsLoaded, setUserPoolsLoaded] = useState(false);
    const [modal, setModal] = useState(false);
    const [idp, setIdp] = useState({} as IConfiguration);
    const [userPools, setUserPools] = useState<ModalUserPools>({
        userPools: [],
        currentUserPoolId: ''
    });
    const [isUserPoolModalOpen, setIsUserPoolModalOpen] = useState(false);
    const integration = useContext(IntegrationContext);

    const client = useMemo(() => ClientFactory.createClient(integration.getTenantId(), integration.getIntegrationType(), integration.getSessionHeaders()), [integration]);
    const userPoolService: UserPoolService = useMemo(() => new UserPoolService(client), [client]);
    const notificationService: NotificationService = NotificationService.getInstance();
    const identityProviderService: IdentityProviderService = useMemo(() => new IdentityProviderService(client), [client]);

    const notifyErrorWithGeneralWarning = useCallback((message: string) => {
        notificationService.notifyError(`${message} ${t('notifications:GENERAL_WARNING')}`);
    }, [notificationService, t]);

    useEffect(() => {
        fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function fetchInitialData() {
        await getPoolProperties();
        await getIdpList();
    }

    async function getPoolProperties() {
        if (poolProperties.userPoolId !== '') {
            setPoolPropertiesLoaded(true);
            return;
        }
        await fetchPoolProperties();
    };

    async function fetchPoolProperties() {
        try {
            const poolProperties = await userPoolService.getPoolProperties();
            return loadSuccessfully(poolProperties);
        } catch(error) {
            if (error instanceof SSOAPINotFoundError) {
                return createPool();
            } else {
                const errorMessage = (error as Error)?.message || t('notifications:NOTIFY_POOL_PARAMS_FAIL_TO_LOAD');
                notifyErrorWithGeneralWarning(errorMessage);
            }
        }
    };

    async function getIdpList() {
        try {
            const configurations = await identityProviderService.getConfigurations();
            setIdpList(configurations);
        } catch(error) {
            if (error instanceof SSOAPINotFoundError) {
                return;
            }
            const errorMessage = (error instanceof Error && error.message) || t('notifications:NOTIFICATION_CONFIGURATION_FAIL_TO_LOAD');
            notifyErrorWithGeneralWarning(errorMessage);
        } finally {
            setIdpListLoaded(true);
        }
    };

    async function createPool() {
        try {
            await userPoolService.createUserPoolMapping();
            await getPoolProperties();
        } catch(error) {
            const errorMessage = (error as Error)?.message || t('notifications:NOTIFY_POOL_PARAMS_FAIL_TO_LOAD');
            notifyErrorWithGeneralWarning(errorMessage);
        }
    };

    async function loadSuccessfully(response: UserPoolPropertiesResponse | void) {
        if (response !== undefined) {
            setPoolProperties({ userPoolId: response.user_pool_id, userPoolSaml2Url: response.user_pool_saml2_url, domain: response.domain, userPoolName: response.user_pool_name, available: true });
            setPoolPropertiesLoaded(true);
        } else {
            notifyErrorWithGeneralWarning(t('notifications:NOTIFY_POOL_PARAMS_FAIL_TO_LOAD'));
        }
    };

    async function saveUserPoolSelection(newUserPoolId: string): Promise<void> {
        try {
            setUserPoolsLoaded(false);
            const poolPropertiesResponse = await userPoolService.putUserPoolMapping({
                current_user_pool_id: userPools.currentUserPoolId,
                new_user_pool_id: newUserPoolId
            });
            loadSuccessfully(poolPropertiesResponse);
            setIdpList([]);
            setIsUserPoolModalOpen(false);
            notificationService.notifySuccess(`${t('notifications:NOTIFICATION_USER_POOL_SWITCH_SUCCESSFUL')}`);
        } catch (error) {
            // TODO: Refactor error handling to use a common error handler to be DRY
            if (error instanceof SSOAPIBadRequest) {
                if (error.getResponseError().error_code === 'USER_POOL_NOT_AVAILABLE') {
                    notificationService.notifyError(t('notifications:NOTIFY_USER_POOL_NOT_AVAILABLE'));
                } else {
                    notifyErrorWithGeneralWarning(t('notifications:NOTIFY_FAILED_TO_SAVE_SELECTED_POOL'));
                }
                await getUserPools();
                await fetchPoolProperties();
                setIsUserPoolModalOpen(true);
            } else if (error instanceof SSOAPINetworkError) {
                notificationService.notifyError(t('notifications:NOTIFICATION_API_ERROR_NETWORK_ERROR'));
            } else if (error instanceof SSOAPIUserSessionInvalidError) {
                notificationService.notifyError(t('notifications:NOTIFICATION_API_ERROR_USER_SESSION_INVALID', { PRODUCT: integration.getIntegrationType() }));
            } else if (error instanceof SSOAPIUserInsufficientPermissionsError) {
                notificationService.notifyError(t('notifications:NOTIFICATION_API_ERROR_USER_INSUFFICIENT_PERMISSION', { PRODUCT_ADMIN_TYPE: integration.getProductAdminType() }));
            } else if (error instanceof SSOAPIServerError) {
                notificationService.notifyError(t('notifications:NOTIFICATION_API_ERROR_GENERIC_SERVER_ERROR', { transaction_id: error.getAPIContext().eventId}));
            } else if (error instanceof SSOAPIUnexpectedResponseBody) {
                notificationService.notifyError(t('notifications:NOTIFICATION_API_ERROR_UNEXPECTED_RESPONSE_BODY'));
            } else {
                const errorMessage = (error as Error)?.message || t('notifications:NOTIFY_FAILED_TO_SAVE_SELECTED_POOL');
                notifyErrorWithGeneralWarning(errorMessage);
            }
        } finally {
            setUserPoolsLoaded(true);
        }
    };

    async function getUserPools(): Promise<void> {
        try {
            setUserPoolsLoaded(false);
            const userPoolsResponse: UserPoolsResponse = await userPoolService.getUserPools();
            setUserPools({
                currentUserPoolId: userPoolsResponse.current_user_pool_id,
                userPools: userPoolsResponse.user_pools
                    .map(pool => {
                        return {
                            userPoolId: pool.user_pool_id,
                            userPoolName: pool.user_pool_name,
                            userPoolSaml2Url: pool.user_pool_saml2_url,
                            domain: pool.domain,
                            available: pool.available
                        };
                    }).sort((a, b) => a.userPoolName.localeCompare(b.userPoolName))
            });
        } catch(error) {
            if (error instanceof SSOAPIBadRequest) {
                notificationService.notifyError(t('notifications:NOTIFICATION_API_ERROR_BAD_REQUEST'));
            } else if (error instanceof SSOAPINetworkError) {
                notificationService.notifyError(t('notifications:NOTIFICATION_API_ERROR_NETWORK_ERROR'));
            } else if (error instanceof SSOAPIUserSessionInvalidError) {
                notificationService.notifyError(t('notifications:NOTIFICATION_API_ERROR_USER_SESSION_INVALID', { PRODUCT: integration.getIntegrationType() }));
            } else if (error instanceof SSOAPIUserInsufficientPermissionsError) {
                notificationService.notifyError(t('notifications:NOTIFICATION_API_ERROR_USER_INSUFFICIENT_PERMISSION', { PRODUCT_ADMIN_TYPE: integration.getProductAdminType() }));
            } else if (error instanceof SSOAPIServerError) {
                notificationService.notifyError(t('notifications:NOTIFICATION_API_ERROR_GENERIC_SERVER_ERROR', { transaction_id: error.getAPIContext().eventId}));
            } else if (error instanceof SSOAPIUnexpectedResponseBody) {
                notificationService.notifyError(t('notifications:NOTIFICATION_API_ERROR_UNEXPECTED_RESPONSE_BODY'));
            } else {
                const errorMessage = (error as Error)?.message || t('notifications:NOTIFY_FAILED_TO_FETCH_POOLS');
                notifyErrorWithGeneralWarning(errorMessage);
            }
            setUserPools({
                userPools: [],
                currentUserPoolId: ''
            });
        } finally {
            setUserPoolsLoaded(true);
        }
    };

    const addSAML = () => {
        setPoolPropertiesLoaded(false);
        const idp = {
            ProviderType: IDPOptions.SAML
        } as IConfiguration;
        setIdp(idp);
        setModal(true);
    };

    const samlEvent = new CustomEvent('samlFormSubmit');
    const saveSAMLForm = () => {
        window.dispatchEvent(samlEvent);
    };

    const close = () => {
        setPoolPropertiesLoaded(true);
        setModal(false);
        setIdpListLoaded(false);
        getIdpList();
        setIdp({} as IConfiguration);
    };

    const [activeTab, setActiveTab] = useState(SSOTabs.POOL);
    const tabClicked = useCallback((tab: Tab): void => {
        setActiveTab(tab.id);
    }, [setActiveTab]);

    const { forceSSOEnabled } = useContext(FeatureFlagContext);

    const tabs: Tab[] = [
        { id: SSOTabs.POOL, title: 'POOL.TITLE', content: <PoolParametersTabContent
            userPools={userPools}
            getUserPools={getUserPools}
            isUserPoolModalOpen={isUserPoolModalOpen}
            setIsUserPoolModalOpen={setIsUserPoolModalOpen}
            saveUserPoolSelection={saveUserPoolSelection}
            currentUserPool={poolProperties}
            poolPropertiesLoaded={poolPropertiesLoaded}
            userPoolsLoaded={userPoolsLoaded}
            fetchPoolProperties={fetchPoolProperties}
        />, featureFlag: false },
    ];

    if (forceSSOEnabled) {
        tabs.push({ id: SSOTabs.LOGIN, title: 'LOGIN.TITLE', content: <LoginOptionsTabContent />, featureFlag: forceSSOEnabled });
    }

    return (
        <>
            <S.Page>
                <S.StyledContainer
                    hideProgressBar={false}
                    newestOnTop={true}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    pauseOnHover
                />
                <S.PageHeader>
                    <S.HeaderSection>
                        <SubHeading fontSize={36} weight={300}>
                            <Trans>IDENTITY_PROVIDER_TITLE</Trans>
                        </SubHeading>
                    </S.HeaderSection>
                    <S.ActionSection>
                        <S.ButtonSection>
                            <S.Button onClick={addSAML}>{t('CONFIGURATION.ADD_SAML_BUTTON_LABEL')} <S.PlusCircleIcon /> </S.Button>
                        </S.ButtonSection>
                    </S.ActionSection>
                </S.PageHeader>
                <S.PageBody>
                    {tabs.length > 1 && (
                        <S.TabContainer>
                            {tabs.map(tab => (
                                <S.TabValue
                                    key={tab.id}
                                    onClick={() => tabClicked(tab)}
                                    isSelected={activeTab === tab.id}
                                    featureFlag={tab.featureFlag}
                                >
                                    <Trans>{tab.title}</Trans>
                                </S.TabValue>
                            ))}
                        </S.TabContainer>
                    )}
                    {tabs.find(tab => tab.id === activeTab)?.content}
                    {poolPropertiesLoaded ? <IdentityProviderList idpList={idpList} setIdpList={setIdpList} idpListLoaded={idpListLoaded} /> : <></>}
                </S.PageBody>

            </S.Page>
            {modal && idp.ProviderType === IDPOptions.SAML ?
                <Popup title={t('CONFIGURATION.SAML_FORM_TITLE')} save={saveSAMLForm} close={close}>
                    <SAMLIdentityProviderDetail update={close} />
                </Popup> : <></> }
        </>
    );
}
